/**
 * Host-owned controller for the clip preview's "Voice: Original | Dubbed | Both"
 * segmented control and its music position.
 *
 * Moving a control writes three values onto the clip: the language, which voice
 * is heard, and whether the recording's own music plays under it. It creates no
 * audio and no tracks. The dubbed audio is worked out by the dub engine every
 * time the clip is turned into a document, so it can never drift out of step
 * with a picture that has been trimmed or cut (PLAT-11511).
 *
 * A press is therefore instant, with nothing on the network behind it, and one
 * Undo press takes it back. What the audio is built FROM is fetched separately
 * and left in a holder inside the editor; its arrival asks for a new document.
 *
 * Several props rather than one callback: the control needs `dubbedLanguages` to
 * appear for a language the story has a dub for, and `appliedDubLanguage` to
 * stay on screen for a clip already playing a dub whatever language the
 * transcript shows. Omitting either hides the control however correctly the
 * callback is wired.
 *
 * It writes no undo code, takes no state copy and asks for no save: the clip
 * adapter replaces `executeCommand` on the live editor and does all three
 * itself. A second copy taken here would put two entries on the user-visible
 * undo list for one action, so the first Undo press would work and the second
 * would restore an identical clip and look dead.
 *
 * It must not switch the Translation Review dub monitor on. That window's own
 * browser-audio dub playback would be heard a second time over a clip that now
 * carries real dubbed audio of its own.
 */
import { CLIP_DUB_APPLY_MESSAGES } from '@src/components/aiVoices/dubApplyMessages';
import {
  type DubLevels,
  FULL_DUB_LEVEL,
} from '@src/components/aiVoices/DubLevelSlider';
import { TIMELINE_MAY_HAVE_MOVED } from '@src/components/aiVoices/useAppliedStoryDub';
import { useDubbedLanguagesRead } from '@src/components/aiVoices/useDubbedLanguages';
import { useDubDescriptionRefresh } from '@src/components/aiVoices/useDubDescriptionRefresh';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { SetDubOptionsCommand } from '@src/editor/commands/SetDubOptionsCommand';
import { normalizeDubVoiceVolume } from '@src/editor/dubbing/DubEngine';
import { sameDubLeadIn } from '@src/editor/dubbing/leadIn';
import {
  sameDubPerLanguageConfiguration,
  segmentLevelsFor,
  withLanguageSegmentLevels,
} from '@src/editor/dubbing/segmentLevels';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useEffect, useState } from 'react';

import type { AudioMode } from '@src/components/aiVoices/AudioModeSegmentedToggle';
import type { MusicSeparationController } from '@src/components/aiVoices/useMusicSeparationController';
import type { ClipVideoCreatorAdapter } from '@src/components/content-components/hooks/ClipVideoCreatorAdapter';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { DubMusicMode } from '@src/editor/dubbing/DubEngine';
import type { DubLeadIn } from '@src/editor/dubbing/leadIn';
import type { DubSegmentLevels } from '@src/editor/dubbing/segmentLevels';
import type { DubOptions } from '@src/editor/models/Timeline';
import type { EditorObserver } from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('component:useClipDubComposition');

/** What a clip that nobody has used the Voice control on reads as. */
const NO_DUB_OPTIONS: DubOptions = {
  language: null,
  voiceMode: 'original',
  musicMode: 'none',
};

/**
 * PLAT-11927 — what Dubbing Review saves onto the clip: what it is playing.
 * `levels` only on Apply to Clip; a level left out stays where the clip has it.
 * `segmentLevels` only on Apply to Line: the whole set of the review's
 * language's lines with their own levels, replacing that language's.
 * `leadIn` only from the Voice picker's lead-in: `null` turns it off.
 */
export interface DubReviewSave {
  voiceMode: AudioMode;
  musicMode: DubMusicMode;
  levels?: Partial<DubLevels>;
  segmentLevels?: DubSegmentLevels;
  leadIn?: DubLeadIn | null;
}

/** Everything about the levels a write carries, the lead-in with them. */
type StoredLevels = Pick<
  DubOptions,
  'voiceVolume' | 'originalVolume' | 'perLanguageConfiguration' | 'leadIn'
>;

/** The stored levels after a change; a level left out stays as stored. */
function mergeDubLevels(
  stored: DubOptions,
  levels: Partial<DubLevels>,
): StoredLevels {
  return {
    perLanguageConfiguration: stored.perLanguageConfiguration,
    leadIn: stored.leadIn,
    voiceVolume:
      levels.voiceVolume === undefined
        ? stored.voiceVolume
        : normalizeDubVoiceVolume(levels.voiceVolume),
    originalVolume:
      levels.originalVolume === undefined
        ? stored.originalVolume
        : normalizeDubVoiceVolume(levels.originalVolume),
  };
}

export interface UseClipDubCompositionOptions {
  adapter: ClipVideoCreatorAdapter;
  editorStore: ClipPreviewEditorStore;
  /** The transcript's selected language. `null` = Original. */
  selectedLanguage: string | null;
  /**
   * The story's voice/music separation state.
   *
   * The SAME controller the Transcript & Speakers tab mounts, called by the host
   * above this hook so it survives the language dropdown closing — separation is
   * a story-wide operation of some minutes, and nobody should have to hold a
   * menu open for it. Two things are read from it: whether its first state read
   * has come back, and the kept bed's signed address.
   *
   * `undefined` on a host that offers no music at all. Such a host is never held
   * waiting and never places a bed.
   */
  musicController?: MusicSeparationController;
}

export interface ClipDubCompositionControl {
  /**
   * The mode the control paints as selected, READ FROM THE CLIP rather than
   * kept as a copy, so it cannot disagree with what the clip is set to.
   */
  audioMode: AudioMode;
  /**
   * The language whose dub is on this clip, or `null` when none is. The Voice
   * control paints whenever this is set, whatever language the transcript is
   * showing — a dub the person cannot see a control for is a dead end.
   */
  appliedDubLanguage: string | null;
  /**
   * The language the control is SHOWING — the applied dub's when the clip
   * carries one, the transcript's selection otherwise. The two differ on an
   * ordinary open from the clip grid, where the transcript reads Original while
   * the clip plays its Russian dub. Anything that must describe the same dub as
   * the control reads this rather than re-deriving it from the transcript's
   * language, which is the mistake that once made every press on such a clip do
   * nothing.
   */
  controlLanguage: string | null;
  /**
   * The list of dubbed languages has not arrived yet, or the separation state
   * has not. The control paints itself as working and unpressable while it is
   * set; presses made in that window are ignored.
   */
  audioModeBusy: boolean;
  /**
   * The handler that writes the options. `undefined` when the AI-Voices flag or
   * the showcase's dubbing setting is off, which is what keeps the whole
   * voice/dub section out of the dropdown on those workspaces.
   */
  onAudioModeChange: ((mode: AudioMode) => void) | undefined;
  /**
   * Puts the music bed on the clip, or takes it off — the same command as the
   * Voice control, moving only the music position and leaving the voice where
   * the clip has it. `undefined` for the same reason `onAudioModeChange` is.
   */
  onMusicModeChange: ((mode: DubMusicMode) => void) | undefined;
  /** The music position the control paints as selected, READ FROM THE CLIP. */
  musicMode: DubMusicMode;
  /** PLAT-11899 — the dubbed voice's level the clip is saved at, 0–100. */
  voiceVolume: number;
  /**
   * PLAT-11899 — save a new level onto the clip, one undo step, leaving the
   * language and both positions where the clip has them. `undefined` when the
   * AI-Voices flag or the showcase's dubbing setting is off.
   */
  onVoiceVolumeChange: ((level: number) => void) | undefined;
  /** PLAT-11927 — the original recording's level on Both, 0–100. */
  originalVolume: number;
  /** PLAT-11927 — save the original's level, as `onVoiceVolumeChange` does. */
  onOriginalVolumeChange: ((level: number) => void) | undefined;
  /** The control language's lines played at their own levels, by `segmentId`. */
  segmentLevels: DubSegmentLevels | undefined;
  /** The original lead-in on Both, or `undefined` when it is off. */
  leadIn: DubLeadIn | undefined;
  /**
   * PLAT-11927 — Dubbing Review's save, as one undo step: its Voice and Music
   * together, so neither is paired with the clip's own other position while
   * the review played its own (a clip on Original opens the review on its
   * unsaved Dubbed defaults), and its levels on Apply to Clip.
   */
  onReviewSave: ((save: DubReviewSave) => void) | undefined;
  /**
   * Fetch the dub's description again, moving NEITHER position.
   *
   * For the one case where the dub itself changed on the server under a clip
   * that is already playing it: a person fills this clip's missing lines, the
   * new audio is written, and nothing this screen observes knows that. The
   * arrival of the fresh description asks for a new document, and the dub is
   * rebuilt from it.
   */
  reapplyComposition: (() => void) | undefined;
  /**
   * Read the story's dubbed-language list again (PLAT-11440).
   *
   * The list is read once when the surface opens, which was enough while every
   * run this screen could start acted on a language already in it. Starting a
   * FIRST dub adds a language to that list on the server, and nothing here
   * observes it: the Voice toggle would stay away, and the button offering the
   * dub would still be on screen after the dub was made.
   */
  refreshDubbedLanguages: (() => void) | undefined;
  /** The BCP-47 codes that already have a dub for this story. */
  dubbedLanguages: string[];
}

export function useClipDubComposition({
  adapter,
  editorStore,
  selectedLanguage,
  musicController,
}: UseClipDubCompositionOptions): ClipDubCompositionControl {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const flags = useFlagsCombination(userIdentity.currentRole);
  const { dubbingEnabled } = useVoiceActivation();
  // A runtime flag AND the showcase's own AI Voices setting — the same
  // combination every dub entry point in the application uses.
  const dubEntryEnabled = !!flags.enableAiVoices && dubbingEnabled;
  const storyId = videoCreator.storyId ?? null;

  // Whether the separation state is an ANSWER yet, as opposed to the "nothing
  // here" the controller reports while its first read is still out. A press
  // landing in that window would write "this story has no music" onto a story
  // that has some. A surface with no controller has nothing to wait for.
  const musicStateSettled = musicController
    ? musicController.status !== 'loading'
    : true;

  // Which languages already have a dub for this story, read through the one
  // shared place the dub surfaces reach the dubbing repository. This screen
  // carried a second, identical copy of that read until PLAT-11440's review;
  // the copy's own gate, request, settling and cleanup were the shared one's,
  // line for line.
  //
  // `settled` says the list is an ANSWER rather than the empty value it holds
  // while the request is out. `controlLanguage` below decides which language a
  // press acts on by asking whether the list contains the language being read,
  // and an empty list is indistinguishable from "this story has no dub in that
  // language" — so a press landing in the loading window would silently write
  // the clip's old language. The control refuses presses until it is true.
  //
  // A FAILED request also settles: the list stays empty and the press falls
  // back to the clip's own language, because keeping the control dead after one
  // network blip would be worse than the defect being fixed.
  const {
    languages: dubbedLanguages,
    settled: dubbedLanguagesSettled,
    // Called after a run that can have ADDED a language to the list.
    refresh: refreshDubbedLanguages,
  } = useDubbedLanguagesRead(storyId);

  // The control's position, READ FROM THE CLIP rather than held as the screen's
  // own copy. A copy always started at Original, so a clip reopened while set
  // to its Russian dub told the person the opposite of what they heard; and a
  // language change reset the copy and nothing else, so switching the
  // transcript to a language with no dub left the dub playing with no control
  // on screen to change it back.
  const readClipOptions = useCallback(
    (): DubOptions => editorStore.editor?.timeline.dubOptions ?? NO_DUB_OPTIONS,
    [editorStore],
  );

  const [applied, setApplied] = useState<DubOptions>(readClipOptions);

  // Re-read only when the answer has changed, so an unrelated clip edit does
  // not re-render the screen.
  const syncFromClip = useCallback(() => {
    setApplied(previous => {
      const next = readClipOptions();
      return previous.language === next.language &&
        previous.voiceMode === next.voiceMode &&
        previous.musicMode === next.musicMode &&
        previous.voiceVolume === next.voiceVolume &&
        previous.originalVolume === next.originalVolume &&
        sameDubPerLanguageConfiguration(
          previous.perLanguageConfiguration,
          next.perLanguageConfiguration,
        ) &&
        sameDubLeadIn(previous.leadIn, next.leadIn)
        ? previous
        : next;
    });
  }, [readClipOptions]);

  useEffect(() => {
    // A reopened clip loads after this hook first renders, so the change
    // notification below is what makes a saved dubbed clip come back reading
    // Dubbed rather than Original.
    syncFromClip();

    const editor = editorStore.editor;
    if (!editor) return undefined;

    // Registration filters by notification type, so this needs no test of its
    // own inside. The project-loaded notification matters most, being what a
    // reopened clip and a restored undo snapshot both arrive through.
    const observer: EditorObserver = { onEditorChange: () => syncFromClip() };
    editor.registerObserver(observer, TIMELINE_MAY_HAVE_MOVED);
    return () => editor.unregisterObserver(observer, TIMELINE_MAY_HAVE_MOVED);
  }, [editorStore, syncFromClip]);

  /**
   * The language a press acts on: the one the control is showing.
   *
   * The applied dub's language when the clip carries a dub, and the transcript's
   * selected language otherwise — which is how a first application starts. The
   * two differ on an ordinary open from the clip grid, where no language travels
   * in the page address, so the transcript reads Original while the clip plays
   * its Russian dub.
   *
   * The language being READ wins whenever the story has a dub for it. Without
   * that, the first language ever applied to a clip won it forever: someone
   * switched the transcript to another language, pressed the dubbed position and
   * heard the first one, with no control anywhere to change it.
   */
  const selectedHasDub =
    !!selectedLanguage && dubbedLanguages.includes(selectedLanguage);
  const controlLanguage = selectedHasDub
    ? selectedLanguage
    : (applied.language ?? selectedLanguage);

  // Keeps the editor's in-memory description filled for the language this clip
  // is set to, which is what the dub engine builds the audio from. Nothing
  // signed reaches the stored clip, so a clip reopened an hour later plays its
  // dub with no repair fetch of its own.
  const { status: descriptionStatus, refreshNow } = useDubDescriptionRefresh({
    editor: editorStore.editor as VideoEditor | undefined,
    storyId,
    language: applied.voiceMode === 'original' ? null : applied.language,
    isOriginalVideoElement: probe => adapter.isOriginalVideoElement(probe),
    musicBedUrl: musicController?.bedUrl ?? null,
    voiceStemUrl:
      musicController?.voiceUrlMp3 ?? musicController?.voiceUrlWav ?? null,
    refreshSource: () => adapter.getVideoSourceStore().refreshSource(),
    enabled: dubEntryEnabled,
  });

  /**
   * One press, whichever control it came from. Every value is passed in as an
   * argument so none can be read back off the screen after the fact — and so
   * none is dropped: the command writes the whole set, so a value left out is
   * a value reset (PLAT-11899 — the levels, on every Voice or Music press).
   */
  const write = useCallback(
    (
      mode: AudioMode,
      musicMode: DubMusicMode,
      language: string | null,
      {
        voiceVolume,
        originalVolume,
        perLanguageConfiguration,
        leadIn,
      }: StoredLevels,
    ) => {
      const editor = editorStore.editor as VideoEditor | undefined;
      if (!editor) return;

      const ran = editor.executeCommand(
        new SetDubOptionsCommand(editor, {
          language,
          voiceMode: mode,
          musicMode,
          voiceVolume,
          originalVolume,
          perLanguageConfiguration,
          leadIn,
        }),
      );
      if (!ran) {
        logger.error('Could not write the dub options onto the clip', {
          language,
          mode,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'Could not apply the dub to this clip. Please try again.',
        });
        return;
      }

      // A press that asked to HEAR a dub is waiting on an answer; a press back
      // to the original voice is waiting on nothing. See the effect below.
      setAwaitingAnswer(mode === 'original' ? null : language);

      syncFromClip();
      // The command's dirty-marking sync is debounced, so the canvas needs this
      // to repaint now.
      adapter.getVideoSourceStore().refreshSource();
    },
    [adapter, editorStore, syncFromClip, videoCreator],
  );

  /**
   * The one failure a person still needs told about: they PRESSED for a dub the
   * story does not have.
   *
   * Said when the answer arrives rather than at the moment of the press,
   * because a press fetches nothing. Without it the control would read Dubbed
   * while the clip played the person's own speech, with nothing on screen
   * saying why.
   *
   * Tied to a press, not to the state. A clip merely OPENED on a language whose
   * dub the story has since lost says nothing: the person hears their own
   * speech, which is what deleting the dub promised them, and a sentence about
   * something they did not just do would be noise.
   */
  const [awaitingAnswer, setAwaitingAnswer] = useState<string | null>(null);
  useEffect(() => {
    if (descriptionStatus !== 'missing') return;
    if (!applied.language || awaitingAnswer !== applied.language) return;
    setAwaitingAnswer(null);

    const message = CLIP_DUB_APPLY_MESSAGES['no-dub'];
    if (message) videoCreator.showToast({ state: 'error', message });
  }, [applied.language, awaitingAnswer, descriptionStatus, videoCreator]);

  // The Voice control moves the voice and leaves the music where the CLIP has
  // it. A fixed `none` here would make every Voice press quietly take away a
  // bed the person had asked for. Read at press time: a first dub's success
  // handler is held from before Keep Original Music wrote it.
  const onAudioModeChange = useCallback(
    (mode: AudioMode) => {
      const current = readClipOptions();
      write(mode, current.musicMode, controlLanguage, current);
    },
    [controlLanguage, readClipOptions, write],
  );

  const onMusicModeChange = useCallback(
    (musicMode: DubMusicMode) => {
      write(applied.voiceMode, musicMode, controlLanguage, applied);
    },
    [applied, controlLanguage, write],
  );

  // PLAT-11899 / PLAT-11927 — the levels alone move. The language stays the
  // one the CLIP plays: the levels belong to the clip, not to whichever
  // language the transcript is showing, so setting one must never switch the
  // dub.
  const onLevelsChange = useCallback(
    (levels: Partial<DubLevels>) => {
      const next = mergeDubLevels(applied, levels);
      if (
        next.voiceVolume === applied.voiceVolume &&
        next.originalVolume === applied.originalVolume
      ) {
        return;
      }
      write(applied.voiceMode, applied.musicMode, applied.language, next);
    },
    [applied, write],
  );
  const onVoiceVolumeChange = useCallback(
    (level: number) => onLevelsChange({ voiceVolume: level }),
    [onLevelsChange],
  );
  const onOriginalVolumeChange = useCallback(
    (level: number) => onLevelsChange({ originalVolume: level }),
    [onLevelsChange],
  );

  // The review's language is the one it plays: the control's, as a Voice
  // press writes. Read at press time, like `onAudioModeChange`.
  const onReviewSave = useCallback(
    ({
      voiceMode,
      musicMode,
      levels = {},
      segmentLevels,
      leadIn,
    }: DubReviewSave) => {
      const current = readClipOptions();
      const merged = mergeDubLevels(current, levels);
      write(voiceMode, musicMode, controlLanguage, {
        ...merged,
        leadIn: leadIn === undefined ? merged.leadIn : (leadIn ?? undefined),
        perLanguageConfiguration:
          segmentLevels === undefined || !controlLanguage
            ? merged.perLanguageConfiguration
            : withLanguageSegmentLevels(
                merged.perLanguageConfiguration,
                controlLanguage,
                segmentLevels,
              ),
      });
    },
    [controlLanguage, readClipOptions, write],
  );

  // Neither position moves: only the description behind them is fetched again.
  const reapplyComposition = useCallback(() => {
    // A clip on Original carries no dub audio, so there is nothing on it that
    // the server's new lines make wrong.
    if (applied.voiceMode === 'original') return;
    refreshNow();
  }, [applied.voiceMode, refreshNow]);

  return {
    audioMode: applied.voiceMode,
    appliedDubLanguage: applied.language,
    controlLanguage,
    audioModeBusy: !dubbedLanguagesSettled || !musicStateSettled,
    onAudioModeChange: dubEntryEnabled ? onAudioModeChange : undefined,
    onMusicModeChange: dubEntryEnabled ? onMusicModeChange : undefined,
    musicMode: applied.musicMode,
    voiceVolume: applied.voiceVolume ?? FULL_DUB_LEVEL,
    onVoiceVolumeChange: dubEntryEnabled ? onVoiceVolumeChange : undefined,
    originalVolume: applied.originalVolume ?? FULL_DUB_LEVEL,
    onOriginalVolumeChange: dubEntryEnabled
      ? onOriginalVolumeChange
      : undefined,
    segmentLevels: segmentLevelsFor(
      applied.perLanguageConfiguration,
      controlLanguage,
    ),
    leadIn: applied.leadIn,
    onReviewSave: dubEntryEnabled ? onReviewSave : undefined,
    reapplyComposition: dubEntryEnabled ? reapplyComposition : undefined,
    refreshDubbedLanguages: dubEntryEnabled
      ? refreshDubbedLanguages
      : undefined,
    dubbedLanguages,
  };
}
